import Bugsnag from '@bugsnag/js'
import { defineStore } from 'pinia'
import { getOrderSchedule } from '@/services/order'
import { AsyncOperationState } from '@/stores/lib/AsyncOperationState'
import type { DeliverySchedule } from '@/services/order'
import { buildFutureOrdersFromDeliveryScheduleResponse } from '@/lib/deliverySchedule'
import { useProductImageStore } from './productImage'

export const useFutureOrderStore = defineStore('futureOrder', {
  state: () => ({
    loadState: AsyncOperationState.IDLE,
    schedule: null as DeliverySchedule | null,
    stale: true as boolean,
  }),
  actions: {
    async init(rechargeId?: string) {
      if (
        (this.stale || this.loadState === AsyncOperationState.IDLE) &&
        rechargeId
      ) {
        await this.load(rechargeId)
      }
    },
    async load(rechargeId: string) {
      const productImageStore = useProductImageStore()
      this.loadState = AsyncOperationState.IN_PROGRESS
      try {
        const response = await getOrderSchedule(rechargeId)
        this.schedule = response
        productImageStore.loadImagesForProducts(
          response.deliveries.flatMap((o) =>
            o.orders.flatMap((li) =>
              li.line_items.map(
                (li) =>
                  `${li.external_product_id.ecommerce}:${li.external_variant_id.ecommerce}`,
              ),
            ),
          ),
        )
        this.loadState = AsyncOperationState.DONE
        this.stale = false
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.loadState = AsyncOperationState.ERRORED
      }
    },
  },
  getters: {
    projectedOrdersMerged: (state) => {
      if (!state.schedule) {
        return []
      }
      return buildFutureOrdersFromDeliveryScheduleResponse(state.schedule)
    },
    getOrderForDate: (state) => (date: string) => {
      if (!state.schedule) {
        return null
      }
      const orders = buildFutureOrdersFromDeliveryScheduleResponse(
        state.schedule,
      )
      return orders.find((o) => o.scheduledAt === date) ?? null
    },
  },
})
